/**
 * PWA Version Manager
 * Simple version tracking for PWA updates using package.json
 */

import { logger } from "@NBCUDTC/reactjs-utility-belt/logger";

import packageJson from "../../package.json";

/**
 * Get the current app version from package.json
 */
export async function getCurrentAppVersion(): Promise<string> {
  try {
    return packageJson.version;
  } catch (error) {
    logger.error("[PWA Version] Error getting current version:", error);
    return "1.0.0"; // Fallback version
  }
}

/**
 * Get version data including checksum
 * In a browser environment, we use the version from package.json
 */
export async function getVersionData() {
  try {
    return {
      version: packageJson.version,
      checksum: "",
      lastUpdate: new Date().toISOString(),
    };
  } catch (error) {
    logger.error("[PWA Version] Error getting version data:", error);
    return null;
  }
}

/**
 * Check if a new version is available by comparing versions
 */
export async function checkForNewVersion(
  currentVersion: string,
): Promise<boolean> {
  try {
    const versionData = await getVersionData();
    if (!versionData) return false;

    // Compare versions
    return versionData.version !== currentVersion;
  } catch (error) {
    logger.error("[PWA Version] Error checking for new version:", error);
    return false;
  }
}

/**
 * Store the current version data
 * In browser environment, this is a no-op as we read from package.json
 */
export async function storeVersionData(
  version: string,
  checksum: string,
): Promise<void> {
  logger.info(
    `[PWA Version] Version: ${version} (checksum: ${checksum.substring(0, 8)}...)`,
  );
}

/**
 * Calculate checksum for current app build
 * Simplified for browser environment
 */
export async function calculateAppChecksum(content: string): Promise<string> {
  try {
    // Simple hash function for browser
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(36);
  } catch (error) {
    logger.error("[PWA Version] Error calculating checksum:", error);
    return "";
  }
}
